import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';

import { Badge as ManagerTeamBadge } from '#src/components/managerTeam/Badge/index.tsx';

import { Badge } from './Badge/index.tsx';
import type { LeagueTrckSlot_so5LeaderboardContender } from './__generated__/index.graphql.ts';

const StyledButtonBase = styled(ButtonBase)`
  max-width: calc(12 * var(--unit));

  &:not(.selected) {
    filter: grayscale(1) brightness(0.6);
  }
`;
const StyledHorizontal = styled(Horizontal).attrs({ center: true })`
  min-width: calc(10 * var(--unit));
`;

type Props = {
  so5LeaderboardContender?: LeagueTrckSlot_so5LeaderboardContender;
  index: number;
  selected: boolean;
  onClick: () => void;
  firstEmptySlot: boolean;
  canCompose: boolean;
  rarity: Rarity;
  completed: boolean;
};

export const LeagueTrckSlot = ({
  index,
  so5LeaderboardContender,
  selected,
  onClick,
  firstEmptySlot,
  canCompose,
  rarity,
  completed,
}: Props) => {
  let badge: ReactNode;

  if (completed) {
    badge = <Badge variant="completed" rarity={rarity} />;
  } else if (so5LeaderboardContender) {
    badge = (
      <ManagerTeamBadge
        leaderboard={so5LeaderboardContender.so5Leaderboard}
        managerTeam={so5LeaderboardContender.managerTeam}
        size={7}
        statusIconSize={4}
      />
    );
  } else if (canCompose && firstEmptySlot) {
    badge = <Badge variant="new" rarity={rarity} />;
  } else {
    badge = <Badge variant="locked" rarity={rarity} />;
  }

  const isLineupValid =
    so5LeaderboardContender?.so5Lineup?.confirmable &&
    !so5LeaderboardContender.so5Lineup.draft;

  return (
    <StyledButtonBase onClick={onClick} className={classNames({ selected })}>
      <Vertical center>
        {badge}
        <StyledHorizontal gap={0.5}>
          {!!isLineupValid && (
            <FontAwesomeIcon
              icon={faCheckCircle}
              color="var(--c-onBase-success)"
              fontSize="var(--intermediate-unit)"
            />
          )}
          <LabelS as="p" bold>
            <Ellipsis>
              {so5LeaderboardContender?.managerTeam?.name || (
                <FormattedMessage
                  defaultMessage="Team {number}"
                  values={{ number: index + 1 }}
                />
              )}
            </Ellipsis>
          </LabelS>
        </StyledHorizontal>
      </Vertical>
    </StyledButtonBase>
  );
};

LeagueTrckSlot.fragments = {
  so5LeaderboardContender: gql`
    fragment LeagueTrckSlot_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        ...ManagerTeamBadge_so5Leaderboard
      }
      so5Lineup {
        id
        confirmable
        draft
      }
      managerTeam {
        id
        hidden
        activeDivisionIconUrl
        name
        ...ManagerTeamBadge_managerTeam
      }
    }
    ${ManagerTeamBadge.fragments.so5Leaderboard}
    ${ManagerTeamBadge.fragments.managerTeam}
  ` as TypedDocumentNode<LeagueTrckSlot_so5LeaderboardContender>,
};
